import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { tokens } from "@/shared/design-system/tokens";
import { useMediaQuery } from "@/shared/hooks/useMediaQuery";
import {
  useSidebarItems,
} from "@/shared/config/navigationConfig";
import { ROUTES } from "@/routes/constants";
import Modal from "../Modal/Modal";
import Header from "../Header/Header";
import Footer from "../Footer";
import { SideBar } from "../SideBar";
import { MobileNavigationDrawer } from "../MobileNavigationDrawer";
import {
  AppShell,
  MainContent,
  NavigationSlot,
  RouteViewport,
} from "./GlobalLayout.styles";

const MOBILE_LAYOUT_QUERY = `(max-width: ${tokens.breakpoints.md - 1}px)`;

const GlobalLayout = () => {
  const location = useLocation();
  const routeViewportRef = useRef<HTMLDivElement>(null);
  const navigationButtonRef = useRef<HTMLButtonElement>(null);
  const wasNavigationOpenRef = useRef(false);
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const isMobileLayout = useMediaQuery(MOBILE_LAYOUT_QUERY);
  const sidebarItems = useSidebarItems();
  const isHome = location.pathname === ROUTES.HOME;
  const shouldShowDesktopSidebar = !isHome && sidebarItems.length > 0;
  const shouldShowMobileNavigation = sidebarItems.length > 0;

  useEffect(() => {
    if (routeViewportRef.current) {
      routeViewportRef.current.scrollTop = 0;
    }

    setIsNavigationOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileLayout) {
      setIsNavigationOpen(false);
    }
  }, [isMobileLayout]);

  useEffect(() => {
    if (
      wasNavigationOpenRef.current &&
      !isNavigationOpen &&
      isMobileLayout
    ) {
      navigationButtonRef.current?.focus();
    }

    wasNavigationOpenRef.current = isNavigationOpen;
  }, [isMobileLayout, isNavigationOpen]);

  const handleNavigationToggle = () => {
    setIsNavigationOpen((isOpen) => !isOpen);
  };

  const handleNavigationClose = () => {
    setIsNavigationOpen(false);
  };

  return (
    <AppShell>
      <Header
        isMobileLayout={isMobileLayout}
        isNavigationOpen={isNavigationOpen}
        showNavigationToggle={shouldShowMobileNavigation}
        onNavigationToggle={handleNavigationToggle}
        navigationButtonRef={navigationButtonRef}
      />

      <MainContent data-testid="app-main-layout">
        <NavigationSlot>
          {isMobileLayout ? (
            <MobileNavigationDrawer
              isOpen={isNavigationOpen}
              items={sidebarItems}
              onClose={handleNavigationClose}
            />
          ) : shouldShowDesktopSidebar ? (
            <SideBar />
          ) : null}
        </NavigationSlot>

        <RouteViewport
          ref={routeViewportRef}
          data-testid="app-route-viewport"
        >
          <Outlet />
        </RouteViewport>
      </MainContent>

      <Footer />
      <Modal />
    </AppShell>
  );
};

export default GlobalLayout;
